import { Button, Heading, Link, Modal, Text } from '@nrx/ui';
import type { JSX } from 'react';

import type { RegistrationConfirmation } from './use-webinar-registration.js';

const TITLE_ID = 'registration-success-title';

type RegistrationConfirmationDialogProps = {
  confirmation: RegistrationConfirmation;
  onClose: () => void;
};

/**
 * Post-registration confirmation: what was booked, where the email lands, and the join link.
 *
 * @remarks Same W4 story as `RegisterWebinarModal` — the library `Modal` supplies the focus trap,
 * Escape-to-close and focus restore the hand-rolled overlay never had.
 *
 * The emphasised values stay in their own `Text` spans rather than being folded into the
 * paragraph: `getByText` matches an element's DIRECT text nodes only, so the confirmation specs
 * reach the title, date and email through those spans. Merging them into the parent paragraph
 * would make three assertions unfindable.
 */
export function RegistrationConfirmationDialog({
  confirmation,
  onClose,
}: RegistrationConfirmationDialogProps): JSX.Element {
  return (
    <Modal open onClose={onClose} aria-labelledby={TITLE_ID}>
      <Heading as="h2" id={TITLE_ID} size="5">
        You&apos;re registered
      </Heading>
      <Text as="p" className="consult-modal__body">
        You&apos;ve been successfully registered for <Text weight="bold">{confirmation.title}</Text>
        {confirmation.dateTimeDisplay ? (
          <>
            {' '}
            on <Text weight="bold">{confirmation.dateTimeDisplay}</Text>
          </>
        ) : null}
        . You should receive a confirmation email with instructions for joining the webinar at{' '}
        <Text weight="bold">{confirmation.email}</Text>.
      </Text>
      {confirmation.joinUrl ? (
        <Text as="p" size="2" className="consult-modal__body">
          When it&apos;s time for the session, use your{' '}
          <Link href={confirmation.joinUrl} target="_blank" rel="noopener noreferrer" underline>
            <Text size="2" weight="medium">
              Zoom join link
            </Text>
          </Link>
          .
        </Text>
      ) : null}
      <Modal.Footer>
        <Button onClick={onClose} className="consult-modal__action">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
